import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { UserService } from '../../user/user.service';
import { DarkMode, UserLanguage, UserModel } from '../../account-settings/models/user.model';
import { IdentityService } from '../../core/identity/identity.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { ThemeService } from './theme.service';
import { CurrentContextService } from '../../core/current-context.service';
import { combineLatest } from 'rxjs';
import { PatchRequest } from '../patch-request.model';
import { UserPatchFields, userPatch } from '../../framework/user-patch';

@Injectable({
  providedIn: 'root',
})
export class QueryParamService {
  constructor(
    private route: Router,
    private readonly userService: UserService,
    private readonly themeService: ThemeService,
    private readonly identityService: IdentityService,
    private readonly snackbar: SnackBarService,
    private readonly currentContextService: CurrentContextService,
  ) {}

  user: UserModel;

  init(user: UserModel): void {
    combineLatest([
      this.route.events.pipe(filter((event) => event instanceof NavigationEnd)), // Router events for navigation
    ]).subscribe(([event]) => {
      // When both user and route events are available
      this.user = user;
      if (!this.user) {
        return;
      }

      const queryParams = this.route.routerState.snapshot.root.queryParams;
      // Normalize query string parameters to lowercase
      const normalizedParams = Object.keys(queryParams).reduce((acc, key) => {
        acc[key.toLowerCase()] = queryParams[key].toLowerCase();
        return acc;
      }, {});

      const language = normalizedParams['language'] || normalizedParams['lang'];
      const theme = normalizedParams['theme'];
      if (!language && !theme) {
        return;
      }

      const changes: UserPatchFields = {
        language: language ? this.mapLanguage(language) : undefined,
        darkMode: theme ? this.mapTheme(theme) : undefined,
      };
      const operations = userPatch(this.user, changes);
      if (operations.length) {
        this.updateUserSettings(operations, changes);
      }
    });
  }

  // Map the language based on query string values
  private mapLanguage(language: string): UserLanguage {
    const swedishValues = ['sv', 'sv-se', 'swe', 'swedish'];
    return swedishValues.includes(language) ? UserLanguage.Swedish : UserLanguage.English;
  }

  // Map the theme based on query string values
  private mapTheme(theme: string): DarkMode {
    const darkModeValues = ['dark', 'darkmode', 'dark-mode'];
    return darkModeValues.includes(theme) ? DarkMode.AlwaysDark : DarkMode.AlwaysLight;
  }

  private updateUserSettings(operations: PatchRequest[], changes: UserPatchFields): void {
    this.userService.patchCurrentUser(operations).subscribe(
      () => {
        if (changes.language) {
          this.user.language = changes.language as UserLanguage;
        }
        if (changes.darkMode) {
          this.user.darkMode = changes.darkMode as DarkMode;
        }
        this.themeService.changeTheme(this.user.darkMode);
        this.identityService.requestUpdateIdentityPayloadAsync();
        this.identityService.notifyPossibleIdentityUpdate();
        this.currentContextService.updateCurrentContextUser({
          ...this.currentContextService.currentContext.user,
          ...this.user,
        });
        this.snackbar.success('settings.profile.profile-update-success');
      },
      () => {
        this.snackbar.error('generic.error');
      },
    );
  }
}
